/** @jsxImportSource @emotion/react */
import { Grid as MuiGrid, Paper as MuiPaper } from '@mui/material';
import { Alert, Buttons } from '@zip/business-components';
import Banners from 'components/banners';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb } from 'types';

import { isSandbox } from 'global';
import * as styles from './BasePage.styles';
import { BasePageProps } from './BasePageProps';

const BasePage: FC<BasePageProps> = ({
  title,
  hasActions = false,
  fullWidth = false,
  fullHeight = false,
  noPaperPadding = false,
  flex = false,
  flexCol = false,
  action,
  bannerProps = undefined,
  sandboxAlert = undefined,
  variant = 'paper',
  breadcrumbs,
  children,
}) => {
  const navigate = useNavigate();
  const handleBreadcrumbClick = (breadcrumb: Breadcrumb): void => {
    if (breadcrumb.pageRoute == null) {
      breadcrumb.onClick();
    } else {
      navigate(breadcrumb.pageRoute);
    }
  };
  return (
    <div css={styles.pageContainer(hasActions, fullHeight)}>
      {breadcrumbs?.length && (
        <MuiGrid container sx={{ flexGrow: 1 }} css={styles.breadcrumbs}>
          {breadcrumbs?.map((breadcrumb, i) => (
            <MuiGrid key={i}>
              <Buttons.Text
                className="body2"
                onClick={(): void => handleBreadcrumbClick(breadcrumb)}
              >
                {breadcrumb.pageName}
              </Buttons.Text>{' '}
              &gt;
            </MuiGrid>
          ))}
          <MuiGrid>
            <Buttons.Text className="body2" css={styles.currentPageBreadcrumb}>
              {title}
            </Buttons.Text>
          </MuiGrid>
        </MuiGrid>
      )}
      <MuiGrid container sx={{ flexGrow: 1 }} justifyContent="space-between">
        <MuiGrid>
          {title && (
            <p className="title3" css={styles.title} data-testid="page-title">
              {title}
            </p>
          )}
        </MuiGrid>
        {action && <MuiGrid>{action}</MuiGrid>}
      </MuiGrid>
      {bannerProps && <Banners.Promo {...bannerProps} css={styles.banner} />}
      {sandboxAlert && isSandbox && (
        <Alert variant="error" css={styles.sandboxAlert}>
          {sandboxAlert}
        </Alert>
      )}
      {variant === 'paper' && (
        <MuiPaper elevation={0} css={styles.paper(fullHeight, noPaperPadding)}>
          <div css={styles.container(fullWidth, fullHeight, flex, flexCol)}>
            {children}
          </div>
        </MuiPaper>
      )}
      {variant === 'layout' && (
        <div css={styles.container(fullWidth, fullHeight, flex, flexCol)}>
          {children}
        </div>
      )}
    </div>
  );
};

export default BasePage;
